/**
 * Schema-neutral type definitions shared by every questionnaire schema:
 * the AI-intake schema (`intakeSchema.ts`), the company-profile baseline
 * sections (`companyProfileSchema.ts`), and the WAFR / FTR module schemas
 * (`wafrSchema.ts` / `ftrSchema.ts`).
 *
 * `intakeSchema.ts` re-exports all of these so its existing importers keep
 * working unchanged.
 */

export type FieldType =
  | { kind: 'text'; placeholder?: string }
  | { kind: 'textarea'; rows?: number; placeholder?: string }
  | { kind: 'number'; placeholder?: string; min?: number; max?: number }
  | { kind: 'radio'; options: ChoiceOption[] }
  | { kind: 'select'; options: ChoiceOption[]; placeholder?: string }
  | { kind: 'multiselect'; options: ChoiceOption[]; allowOther?: boolean }
  | { kind: 'list'; placeholder?: string }
  | { kind: 'eligibility-grid'; rows: ChoiceOption[] }
  | { kind: 'use-cases-table' }
  | { kind: 'qa-list' }
  | { kind: 'contacts-table' }

export interface ChoiceOption {
  value: string
  label: string
  hint?: string
  /** Optional long-form help body shown in the modal alongside the short hint. */
  help?: string
}

export interface FieldDef {
  key: string
  label: string
  /** One-paragraph summary. Shown at the top of the help modal as a quick read. */
  hint: string
  /** Optional long-form, beginner-friendly explanation. Supports paragraphs,
   *  bullets (`- item`), bold (`**text**`), inline code (` `x` `), and headings
   *  (`## Title`). Written in plain English with the assumption that the
   *  reader hasn't seen the AWS jargon before. */
  help?: string
  type: FieldType
  /** Priority signal driven by the AWS program PDFs (Funding Benefit Guide,
   *  Quick Reference, Sprint 1 Training).
   *  - `required` — hard requirement: AWS rejects the Fund Request without it,
   *    or the assessment cannot be claimed as delivered.
   *  - `recommended` — strongly advised for a quality assessment; the LLM-free
   *    deterministic renderers depend on these to produce useful output. */
  priority?: 'required' | 'recommended'
  required?: boolean
  /** Render at half width: consecutive `half` fields pair up two per line
   *  (e.g. HQ city + state, postal + country). Ignored by the two-column
   *  form layout, which already grids everything. */
  half?: boolean
}

/** Group a field list into render rows: consecutive `half` fields pair up
 *  two per row; everything else gets its own full-width row. Shared by the
 *  single-column form renderers (SectionFieldsForm, the public onboarding
 *  page) so paired fields lay out identically everywhere. */
export function fieldRows(fields: FieldDef[]): FieldDef[][] {
  const rows: FieldDef[][] = []
  let i = 0
  while (i < fields.length) {
    const f = fields[i]!
    if (f.half && fields[i + 1]?.half) {
      rows.push([f, fields[i + 1]!])
      i += 2
    } else {
      rows.push([f])
      i += 1
    }
  }
  return rows
}

/** Which phase of the AI-intake workflow this section is collected in.
 *  - `company` — the company-profile prerequisite (company basics); lives on the
 *    standalone Company profile page, never rendered inside the intake — but its
 *    required fields still gate intake finalize (the intake rail links to it).
 *  - `intake` — shown on the chat-style intake page; profile is generated when these are done.
 *  - `proposals` — the use-case hypotheses, shown on the dedicated Use cases stage page
 *    (its breadcrumb ✓ is gated on capturing at least 3 use cases).
 *  - `engagement` — shown on the dedicated Engagement page that follows Proposals
 *    (the AWS funding paperwork + ACE opportunity), gated on having ≥1 proposal. */
export type Phase = 'company' | 'intake' | 'proposals' | 'engagement'

export interface SectionDef {
  key: string
  number: number
  title: string
  /** Short label for the section sidebar so it renders on one line; the form
   *  pane heading always shows the full `title`. Falls back to `title`. */
  navTitle?: string
  description?: string
  /** Longer beginner-friendly explanation of what the whole section is for,
   *  rendered as markdown in a help modal beside the section title. */
  help?: string
  fields: FieldDef[]
  /** AI-intake workflow phase. Only the AI-intake sections participate in the
   *  intake workflow — company-profile and WAFR/FTR module sections omit it. */
  phase?: Phase
  /** Where the section's values are stored.
   *  - `company` — on the shared company profile (read/written by every module).
   *  - `module` (or absent) — on the owning module's own instance. */
  target?: 'company' | 'module'
}
